import React, { useMemo } from 'react';
import { ColumnDef, TableMeta } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { formatMetricValue, MetricData } from 'helpers/utils';
import ExcelJS from 'exceljs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { Row, Col } from 'react-bootstrap';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';

interface PerformanceData {
  bxpcampaignid: number;
  date: string;
  // eslint-disable-next-line
  campaigndata: any;
}

export const campaignPerformanceDataTableColumns = (
  data: PerformanceData[]
): ColumnDef<PerformanceData>[] => {
  const fixedColumns: ColumnDef<PerformanceData>[] = [
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row: { original } }) =>
        new Date(original.date).toLocaleDateString(),
      meta: {
        headerProps: { style: { width: '5%' }, className: 'pe-2 ps-0 fs-10' },
        cellProps: { className: 'py-0 pe-2 ps-0 white-space-nowrap' }
      }
    }
  ];

  if (data.length === 0) return fixedColumns;

  const firstRecord = data[0];
  const campaignData = firstRecord.campaigndata || {};
  const metricKeys = Object.keys(campaignData);

  const dynamicColumns: ColumnDef<PerformanceData>[] = metricKeys.map(key => ({
    accessorFn: row => row.campaigndata[key],
    header: key,
    enableSorting: false,
    cell: ({ getValue }) => {
      const metric = getValue() as MetricData;
      return formatMetricValue(metric);
    },
    meta: {
      headerProps: {
        style: { width: '5%' },
        className: 'px-1 fs-10 white-space-nowrap text-center'
      },
      cellProps: { className: 'px-1 white-space-nowrap text-center' }
    }
  }));

  return [...fixedColumns, ...dynamicColumns];
};

export const HVACampaignPerformanceDataTable = () => {
  const table = useAdvanceTableContext();
  const data = table.options.data as PerformanceData[];

  const tableOptions = useMemo(
    () => ({
      meta: {} as TableMeta<PerformanceData>
    }),
    []
  );

  const handleExportToExcel = async () => {
    // Crear workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Performance Data');

    // Preparar encabezados
    const headers = ['Date'];
    if (data.length > 0) {
      Object.keys(data[0].campaigndata || {}).forEach(key => headers.push(key));
    }

    // Configurar columnas
    worksheet.columns = headers.map(header => ({
      header: header,
      key: header,
      width: Math.max(header.length, 15)
    }));

    // Agregar datos
    data.forEach(row => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowData: Record<string, any> = {
        Date: new Date(row.date).toLocaleDateString()
      };

      Object.entries(row.campaigndata).forEach(([key, value]) => {
        rowData[key] = formatMetricValue(value as MetricData);
      });

      worksheet.addRow(rowData);
    });

    // Estilizar encabezado
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Generar y descargar
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campaign_performance_${
      new Date().toISOString().split('T')[0]
    }.xlsx`;
    link.click();

    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div>
        <Row className="d-flex justify-content-between align-items-center">
          <Col>
            <h5>Raw Data</h5>
          </Col>
          <Col className="d-flex justify-content-end">
            <Button
              className="pt-0 pb-0"
              variant="link"
              onClick={handleExportToExcel}
              disabled={!data.length}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2 fs-9" />
              Download to Excel
            </Button>
          </Col>
        </Row>
      </div>
      <AdvanceTable
        tableProps={{
          size: 'sm',
          className: 'phoenix-table fs-9',
          ...tableOptions
        }}
        rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
      />
      <AdvanceTableFooter pagination />
    </div>
  );
};
